import { client } from "@/hono";
import { useLoading, useStream } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import type { ModalProps } from "@msuite/ui/modal";
import { Portal } from "@msuite/ui/portal";
import { responseHandler } from "@msuite/ui/utils";
import {
	postAskInsightSchema,
	type InsightHistoryItemSchema,
	type PostAskInsightSchema,
} from "@msuite/utils/schemas";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { InsightsSpotlightBackdrop } from "./insights-spotlight-backdrop";
import { InsightsSpotlightInput } from "./insights-spotlight-input";
import type { ChatItem, ChatItemInput } from "./shared";
import { InsightsSpotlightChat } from "./insights-spotlight-chat";
import { generateId } from "@msuite/utils/strings";

/** Number of most recent question/answer turns sent as conversation context with each request. */
const MAX_HISTORY_TURNS = 10;

/** Props Interface */
interface InsightsSpotlightProps extends ModalProps {}

export function InsightsSpotlight({ onClose }: InsightsSpotlightProps) {
	/** Hooks */
	const searchLoader = useLoading();
	const methods = useForm({
		resolver: resolver(postAskInsightSchema),
	});
	const streamingAnswerId = useRef<string | null>(null);
	const summaryStream = useStream(`${process.env.PUBLIC_SERVER_URL}/insights/summarize`, {
		startStreamOnMount: false,
		resetDataOnInput: false,
		onMessage: (chunk) => appendToAnswer(streamingAnswerId.current, chunk),
	});

	/** State */
	const [chat, setChat] = useState<ChatItem[]>([]);

	/** Functions */
	function findLastThinkingIndex(items: ChatItem[]) {
		for (let i = items.length - 1; i >= 0; i--) {
			if (items[i]?.type === "thinking") return i;
		}
		return null;
	}

	function buildHistory(items: ChatItem[]): InsightHistoryItemSchema[] {
		const history: InsightHistoryItemSchema[] = [];
		let pendingQuestion: string | null = null;
		for (const item of items) {
			if (item.type === "question") {
				pendingQuestion = item.item.question;
			} else if (item.type === "answer" && pendingQuestion !== null) {
				history.push({ question: pendingQuestion, sql: item.item.sql, summary: item.item.summary });
				pendingQuestion = null;
			}
		}
		return history.slice(-MAX_HISTORY_TURNS);
	}

	function appendToAnswer(id: string | null, chunk: string) {
		if (!id) return;
		setChat((prev) =>
			prev.map((entry) =>
				entry.id === id && entry.type === "answer"
					? { ...entry, item: { ...entry.item, summary: entry.item.summary + chunk } }
					: entry,
			),
		);
	}

	function handleOnMessage(item: ChatItemInput): string {
		const id = generateId();
		setChat((prev) => {
			if (item.type === "answer") {
				const thinkingIndex = findLastThinkingIndex(prev);
				if (thinkingIndex !== null) {
					const next = [...prev];
					if (!next[thinkingIndex]) return next;
					next[thinkingIndex] = { id, ...item };
					return next;
				}
			}
			const chatItem: ChatItem = { id, ...item };
			return [...prev, chatItem];
		});
		return id;
	}

	async function handleOnSubmit(json: PostAskInsightSchema) {
		if (searchLoader.isLoading) return;
		const initialQuestion = json.question;
		if (!json.question.trim()) return;
		const history = buildHistory(chat);
		handleOnMessage({ type: "question", item: json });
		methods.reset({ question: "" });
		try {
			searchLoader.start();
			setTimeout(() => {
				handleOnMessage({ type: "thinking", item: null });
			}, 400);
			const response = await client.insights.ask.$post({ json: { ...json, history } });
			const data = await responseHandler(response, false);
			const answerId = handleOnMessage({ type: "answer", item: data.answer });

			if (data.answer.status !== "cant_answer") {
				streamingAnswerId.current = answerId;
				await summaryStream.startStream({
					body: {
						question: initialQuestion,
						sql: data.answer.sql,
						data: data.answer.data,
						history,
					},
				});
			}
		} catch (error) {
			console.error(error);
			methods.reset({ question: initialQuestion });
		} finally {
			searchLoader.stop();
			streamingAnswerId.current = null;
		}
	}

	/** Render */
	return (
		<Portal>
			<InsightsSpotlightBackdrop onClose={onClose} />
			<InsightsSpotlightChat
				items={chat}
				onClose={onClose}
			/>
			<FormProvider {...methods}>
				<InsightsSpotlightInput
					onSubmit={handleOnSubmit}
					loader={searchLoader}
					onClose={onClose}
				/>
			</FormProvider>
		</Portal>
	);
}
